import { useState } from 'react';
import { Badge, Checkbox, ConfigProvider, Flex, Typography, theme } from 'antd';
import { Line } from 'recharts';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import { useFilterContext } from '@/context';
import { SelectEcho } from '@/components/echo/primitives';
import { useUniquesQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import { createGranularityXAxisFormatter, formatTooltipDate, uppercaseFirstLetter } from '@/utils';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import type { ChartGranularity } from '@/types';
import type { UniquesTimeSeriesResponse, UniquesTimeSeriesPoint } from '@/types/api/uniques';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';

type VisibleDataType = 'viewers' | 'listeners' | 'listenersAndViewers';

interface UniqueVariant {
  description: string;
  value: VisibleDataType;
  color: string;
}

const UNIQUE_VIEWERS_DATA_START = '2026-09-01';
/** Latest week-end date (`dt`) whose trailing 7-day window `[dt-6, dt]` still includes 1 September 2026. */
const UNIQUE_VIEWERS_WEEK_WINDOW_END = '2026-09-07';
const UNIQUE_VIEWERS_DATA_START_MESSAGE =
  'Unique viewer data is only available from 1st September 2026 onwards';

const isUniqueViewersDataStartPoint = (date: string, granularity: ChartGranularity): boolean => {
  if (granularity === 'month') {
    return date.startsWith('2026-09');
  }
  if (granularity === 'week') {
    return date >= UNIQUE_VIEWERS_DATA_START && date <= UNIQUE_VIEWERS_WEEK_WINDOW_END;
  }
  return date === UNIQUE_VIEWERS_DATA_START;
};

const UNIQUES_VARIANTS: UniqueVariant[] = [
  {
    description: 'Total listeners and viewers on podcast platforms',
    value: 'listenersAndViewers',
    color: '#0066cc',
  },
  { description: 'Listeners on podcast platforms', value: 'listeners', color: '#e557ba' },
  { description: 'Viewers on podcast platforms', value: 'viewers', color: '#605cff' },
];
const LISTENERS_ONLY_VARIANTS = UNIQUES_VARIANTS.filter(({ value }) => value === 'listeners');

interface UniquesOverTimeCardEchoProps {
  hasHlsInSelection: boolean;
}

const UniquesOverTimeCardEcho = ({ hasHlsInSelection }: UniquesOverTimeCardEchoProps) => {
  const isBeforeDataAvailability = useIsBeforeDataAvailability('uniques');
  const [granularity, setGranularity] = useState<ChartGranularity>('day');
  const { filters } = useFilterContext();
  const hasExactlyOneShow = filters.selectedShows.length === 1;
  const { queryResult } = useFetchQuery(
    useUniquesQuery,
    // TODO: Remove this once we enable network level and multi show support on audience page
    { enabled: hasExactlyOneShow },
    { granularity }
  );

  return (
    <CardShellEcho
      testId="uniques-over-time-card"
      title="Uniques"
      headerOptions={
        <SelectEcho
          style={{ minWidth: 100 }}
          value={granularity}
          onChange={(value) => setGranularity(value as ChartGranularity)}
          options={[
            { label: 'Daily', value: 'day' },
            { label: 'Weekly', value: 'week' },
            { label: 'Monthly', value: 'month' },
          ]}
        />
      }
    >
      {isBeforeDataAvailability ? (
        <DataAvailabilityBanner dataType="uniques" />
      ) : (
        <CardContent
          variant="echo"
          {...queryResult}
          isPending={hasExactlyOneShow && queryResult.isPending}
          renderData={(data: UniquesTimeSeriesResponse) => (
            <Data
              data={hasExactlyOneShow ? data : []}
              granularity={granularity}
              hasHlsInSelection={hasHlsInSelection}
            />
          )}
        />
      )}
    </CardShellEcho>
  );
};

export const UniquesChartTooltipContent = ({
  payload,
  visibleVariants,
  granularity,
}: {
  payload: UniquesTimeSeriesPoint;
  visibleVariants: UniqueVariant[];
  granularity: ChartGranularity;
}) => {
  const { token } = theme.useToken();
  if (!payload.date) return null;

  const showViewerDataStartNote = isUniqueViewersDataStartPoint(payload.date, granularity);

  return (
    <Flex vertical gap={token.marginXXS} style={{ maxWidth: 260 }}>
      <Typography.Text strong style={{ color: token.colorTextLightSolid }}>
        {formatTooltipDate(payload.date, granularity)}
      </Typography.Text>
      {visibleVariants.map((variant) => (
        <Flex key={variant.value} vertical gap={token.marginXXS}>
          <Flex align="center" gap={token.marginXXS}>
            <Badge color={variant.color} />
            <Typography.Text style={{ color: token.colorTextLightSolid }}>
              {uppercaseFirstLetter(variant.value.replace(/([A-Z])/g, ' $1').toLowerCase())}:
            </Typography.Text>
            <Typography.Text
              strong
              style={{ color: token.colorTextLightSolid, marginInlineStart: 'auto' }}
            >
              {formatBigNumbers(payload[variant.value])}
            </Typography.Text>
          </Flex>
          {showViewerDataStartNote && variant.value === 'viewers' && (
            <Typography.Text
              style={{
                color: token.colorTextLightSolid,
                fontSize: token.fontSizeSM,
                opacity: 0.875,
                paddingLeft: 10,
              }}
            >
              ({UNIQUE_VIEWERS_DATA_START_MESSAGE})
            </Typography.Text>
          )}
        </Flex>
      ))}
    </Flex>
  );
};

const Data = ({
  data,
  granularity,
  hasHlsInSelection,
}: {
  data: UniquesTimeSeriesResponse;
  granularity: ChartGranularity;
  hasHlsInSelection: boolean;
}) => {
  const [visibleDataTypes, setVisibleDataTypes] = useState<Record<VisibleDataType, boolean>>({
    viewers: true,
    listeners: true,
    listenersAndViewers: true,
  });
  const availableVariants = hasHlsInSelection ? UNIQUES_VARIANTS : LISTENERS_ONLY_VARIANTS;
  const visibleVariants = availableVariants.filter(({ value }) => visibleDataTypes[value]);
  const formatXAxisDate = createGranularityXAxisFormatter(granularity);
  if (data.length === 0)
    return <Typography.Text type="secondary">No uniques data available</Typography.Text>;

  return (
    <Flex vertical>
      <TimeSeriesLineChart<UniquesTimeSeriesPoint>
        data={data}
        dateKey="date"
        xAxisTickFormatter={formatXAxisDate}
        yAxisFormatter={(value) => (value !== 0 ? formatBigNumbers(value) : '')}
        ariaLabel="Unique listeners and viewers over time"
        tooltipContent={(payload) => (
          <UniquesChartTooltipContent
            payload={payload}
            visibleVariants={visibleVariants}
            granularity={granularity}
          />
        )}
        height={300}
      >
        {visibleVariants.map((variant) => (
          <Line
            key={variant.value}
            name={variant.description}
            dataKey={variant.value}
            stroke={variant.color}
            dot={false}
            connectNulls
            isAnimationActive
            type="monotone"
            strokeWidth={2}
          />
        ))}
      </TimeSeriesLineChart>
      <Flex justify="center" gap={10}>
        {availableVariants.map((variant) => (
          <ConfigProvider
            key={variant.value}
            theme={{ token: { colorPrimary: variant.color, colorPrimaryHover: variant.color } }}
          >
            <Checkbox
              onChange={() =>
                setVisibleDataTypes((previous) => ({
                  ...previous,
                  [variant.value]: !previous[variant.value],
                }))
              }
              checked={visibleDataTypes[variant.value]}
            >
              {variant.description}
            </Checkbox>
          </ConfigProvider>
        ))}
      </Flex>
    </Flex>
  );
};

export default UniquesOverTimeCardEcho;
